import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuthStore } from "../store/authStore";
import { authService } from "../services/authService";
import { disconnectEcho } from "../services/echoService";
import {
  isBiometricAvailable,
  isBiometricEnabled,
  registerBiometric,
  disableBiometric,
} from "../services/biometricService";
import {
  isNotificationSupported,
  isNotificationEnabled,
  enableNotifications,
  disableNotifications,
  getStoredFCMToken,
} from "../services/fcmService";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Package,
  UserCog,
  Fingerprint,
  Bell,
  LogOut,
  ChevronRight,
  CheckCircle,
  Loader2,
  Headset,
} from "lucide-react";
import { isHelpRequestEnabled } from "../utils/featureFlags";

function Settings() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const selectedProductId = useAuthStore((state) => state.selectedProductId);
  const selectedRoleId = useAuthStore((state) => state.selectedRoleId);
  const productDetails = useAuthStore((state) => state.productDetails);
  const setProductRole = useAuthStore((state) => state.setProductRole);
  
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricActive, setBiometricActive] = useState(false);
  const [isSettingUpBiometric, setIsSettingUpBiometric] = useState(false);
  const [notificationsAvailable, setNotificationsAvailable] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [isTogglingNotifications, setIsTogglingNotifications] = useState(false);
  
  // Product/Role selection modal
  const [isChangingProduct, setIsChangingProduct] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    checkBiometric();
    checkNotifications();
    
    const handleFocus = () => {
      checkNotifications();
    };
    
    const handleStorage = (e) => {
      if (e.key === 'fcm_token') {
        checkNotifications();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    window.addEventListener('storage', handleStorage);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const checkBiometric = async () => {
    const available = await isBiometricAvailable();
    const enabled = isBiometricEnabled();
    setBiometricAvailable(available);
    setBiometricActive(enabled);
  };

  const checkNotifications = () => {
    const available = isNotificationSupported();
    const enabled = isNotificationEnabled() && !!getStoredFCMToken();
    setNotificationsAvailable(available);
    setNotificationsEnabled(enabled);
  };

  const handleToggleBiometric = async () => {
    if (biometricActive) {
      disableBiometric();
      setBiometricActive(false);
      toast.success("Biometric authentication disabled");
    } else {
      setIsSettingUpBiometric(true);
      try {
        await registerBiometric();
        setBiometricActive(true);
        toast.success("Biometric authentication enabled!");
      } catch (error) {
        console.error("Biometric setup failed:", error);
        toast.error("Failed to setup biometric authentication");
      } finally {
        setIsSettingUpBiometric(false);
      }
    }
  };

  const handleToggleNotifications = async () => {
    if (notificationsEnabled) {
      setIsTogglingNotifications(true);
      try {
        await disableNotifications();
        setNotificationsEnabled(false);
        toast.success("Push notifications disabled");
      } catch (error) {
        console.error("Failed to disable notifications:", error);
        toast.error("Failed to disable notifications");
      } finally {
        setIsTogglingNotifications(false);
      }
    } else {
      setIsTogglingNotifications(true);
      try {
        await enableNotifications();
        checkNotifications();
        toast.success("Push notifications enabled!");
      } catch (error) {
        console.error("Failed to enable notifications:", error);
        toast.error(error.message || "Failed to enable notifications");
      } finally {
        setIsTogglingNotifications(false);
      }
    }
  };

  const handleChangeProduct = async () => {
    setIsChangingProduct(true);
    setLoadingProducts(true);
    try {
      const data = await authService.getRolesAndProducts();
      if (data.success && data.data?.products) {
        setProducts(data.data.products);
        // Pre-select current product
        const currentProduct = data.data.products.find(p => p.id === selectedProductId);
        if (currentProduct) {
          setSelectedProduct(currentProduct);
          const currentRole = currentProduct.roles?.find(r => r.id === selectedRoleId);
          if (currentRole) {
            setSelectedRole(currentRole);
          }
        }
      } else {
        toast.error("No products or roles available");
      }
    } catch (error) {
      console.error("Failed to fetch roles and products:", error);
      toast.error("Failed to load products and roles");
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setSelectedRole(null); // Reset role when product changes
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
  };

  const handleConfirmProductChange = () => {
    if (!selectedProduct || !selectedRole) {
      toast.error("Please select both product and role");
      return;
    }

    setProductRole(selectedProduct.id, selectedRole.id, {
      productName: selectedProduct.product_name,
      roleName: selectedRole.name,
      userDivision: selectedRole.user_division,
    });

    toast.success(`Switched to ${selectedProduct.product_name} - ${selectedRole.name}`);
    setIsChangingProduct(false);
    
    // Refresh the page to apply new product context
    window.location.reload();
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      disconnectEcho();
      logout();
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 transition-colors duration-300 flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-20 backdrop-blur-xl bg-white/80 dark:bg-gray-900/80 border-b border-white/20 dark:border-gray-800 shadow-lg safe-top transition-colors duration-300">
        <div className="px-6 py-5 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 rounded-2xl shadow-lg hover:shadow-xl transition-all active:scale-95"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Settings
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6 pb-32">
        {/* User Info Section */}
        <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 mb-6">
          <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
            Account
          </h3>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg">
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{user?.name}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Product & Role Section */}
        {selectedProductId && (
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 mb-6">
            <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
              Current Selection
            </h3>
            
            <button
              onClick={handleChangeProduct}
              className="w-full flex items-center justify-between p-4 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-2xl border-2 border-orange-200 dark:border-orange-800 hover:border-orange-300 dark:hover:border-orange-700 transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Product</p>
                  <p className="text-sm text-gray-900 dark:text-gray-100 font-bold">
                    {productDetails?.productName || 'Not selected'}
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>

            {productDetails?.roleName && (
              <div className="mt-3 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <UserCog className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Role</p>
                    <p className="text-sm text-gray-900 dark:text-gray-100 font-bold">
                      {productDetails.roleName}
                    </p>
                    {productDetails.userDivision && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Division: {productDetails.userDivision}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* App Settings Section */}
        <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 mb-6">
          <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
            Preferences
          </h3>

          {/* Push Notifications */}
          {notificationsAvailable && (
            <div className="flex items-center justify-between py-4 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-900 dark:to-cyan-900 rounded-xl flex items-center justify-center">
                  <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                    Push Notifications
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Receive real-time updates
                  </p>
                </div>
              </div>
              <button
                onClick={handleToggleNotifications}
                disabled={isTogglingNotifications}
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                  notificationsEnabled
                    ? "bg-gradient-to-r from-blue-500 to-cyan-500"
                    : "bg-gray-300 dark:bg-gray-600"
                } ${isTogglingNotifications ? "opacity-50" : ""}`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                    notificationsEnabled ? "translate-x-7" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          )}

          {/* Biometric Authentication */}
          {biometricAvailable && (
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-indigo-100 dark:from-purple-900 dark:to-indigo-900 rounded-xl flex items-center justify-center">
                  <Fingerprint className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                    Biometric Lock
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Use Face ID or fingerprint
                  </p>
                </div>
              </div>
              <button
                onClick={handleToggleBiometric}
                disabled={isSettingUpBiometric}
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                  biometricActive
                    ? "bg-gradient-to-r from-purple-500 to-indigo-500"
                    : "bg-gray-300 dark:bg-gray-600"
                } ${isSettingUpBiometric ? "opacity-50" : ""}`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                    biometricActive ? "translate-x-7" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          )}
        </div>

        {/* Support Section */}
        {isHelpRequestEnabled() && (
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 mb-6">
            <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
              Support
            </h3>
            <button
              onClick={() => navigate("/help-requests")}
              className="w-full flex items-center justify-between p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl border border-green-200 dark:border-green-800 hover:border-green-300 dark:hover:border-green-700 transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Headset className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-sm text-gray-900 dark:text-gray-100 font-bold">
                    My Help Requests
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    View and track your support tickets
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        )}

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white rounded-3xl shadow-lg hover:shadow-xl transition-all active:scale-95 font-bold"
        >
          <LogOut className="w-5 h-5" />
          Logout
        </button>
      </div>

      {/* Product/Role Selection Modal */}
      {isChangingProduct && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Change Product & Role
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Select a new product and role to continue
              </p>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingProducts ? (
                <div className="text-center py-8">
                  <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">Loading products...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Product Selection */}
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <Package className="w-5 h-5 text-orange-500" />
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                        Select Product
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {products.map((product) => (
                        <button
                          key={product.id}
                          onClick={() => handleProductSelect(product)}
                          className={`p-4 rounded-xl border-2 transition-all duration-200 text-left relative ${
                            selectedProduct?.id === product.id
                              ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                              : "border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-700"
                          }`}
                        >
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {product.product_name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {product.roles?.length || 0} role(s)
                          </div>
                          {selectedProduct?.id === product.id && (
                            <CheckCircle className="w-5 h-5 text-orange-500 absolute top-4 right-4" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Role Selection */}
                  {selectedProduct && selectedProduct.roles && selectedProduct.roles.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <UserCog className="w-5 h-5 text-orange-500" />
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                          Select Role
                        </h3>
                      </div>
                      <div className="grid grid-cols-1 gap-3">
                        {selectedProduct.roles.map((role) => (
                          <button
                            key={role.id}
                            onClick={() => handleRoleSelect(role)}
                            className={`p-4 rounded-xl border-2 transition-all duration-200 text-left relative ${
                              selectedRole?.id === role.id
                                ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                                : "border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-700"
                            }`}
                          >
                            <div className="font-semibold text-gray-900 dark:text-white">
                              {role.name}
                            </div>
                            {role.user_division && (
                              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                Division: {role.user_division}
                              </div>
                            )}
                            {selectedRole?.id === role.id && (
                              <CheckCircle className="w-5 h-5 text-orange-500 absolute top-4 right-4" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3">
              <button
                onClick={() => setIsChangingProduct(false)}
                className="flex-1 px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-2xl font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmProductChange}
                disabled={!selectedProduct || !selectedRole}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl hover:from-orange-600 hover:to-amber-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;
